<template>
  <Block class="view-header">
    <div class="view-header__content">
      <div class="view-header__col view-header__col--content">
        <h1 v-if="finalTitle" class="view-header__title" v-html="finalTitle" />
        <nav
          v-if="breadcrumbs.length > 1"
          class="breadcrumbs"
          :aria-label="$t('breadcrumbs.ariaLabel')"
        >
          <ol class="breadcrumbs__list">
            <li
              v-for="(item, index) of breadcrumbs"
              :key="item.path"
              class="breadcrumbs__list-item"
            >
              <span
                v-if="index === breadcrumbs.length - 1"
                class="breadcrumbs__item breadcrumbs__item--current"
                aria-current="page"
                v-html="item.title"
              />

              <template v-else>
                <router-link
                  :to="{ path: item.path }"
                  class="breadcrumbs__item breadcrumbs__item--link"
                >
                  <span v-html="item.title" />
                </router-link>
                <span class="breadcrumbs__separator" aria-hidden="true">
                  »
                </span>
              </template>
            </li>
          </ol>
        </nav>
      </div>
      <div
        v-if="!!slots.tools"
        class="view-header__col view-header__col--tools"
      >
        <slot name="tools" />
      </div>
    </div>

    <div v-if="!!slots.footer" class="view-header__footer">
      <slot name="footer" />
    </div>
  </Block>
</template>

<script setup lang="ts">
import type { Breadcrumb } from '@/types'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    title?: string
    dynamicBreadcrumbs?: Breadcrumb[]
  }>(),
  {
    title: undefined,
    dynamicBreadcrumbs: () => [],
  },
)

const slots = defineSlots<{
  footer(): any
  tools(): any
}>()

const route = useRoute()
const { t, te } = useI18n()

const finalTitle = computed<string | undefined>(() => {
  if (props.title) {
    return props.title
  }

  if (route.meta.title) {
    return String(route.meta.title)
  }

  const translationKey = `routes.${String(route.name)}.title`
  if (te(translationKey)) {
    return t(translationKey)
  }

  return undefined
})

const routeBreadcrumbs = computed<Breadcrumb[]>(() => {
  return route.matched.reduce((acc, item) => {
    if (item.meta.hideFromBreadcrumbs === true) {
      return acc
    }

    acc.push({
      path: item.path,
      title: ((): string => {
        const translationKey = `routes.${String(item.name)}.menuTitle`
        if (te(translationKey)) {
          return t(translationKey)
        }

        return String(route.name)
      })(),
    })

    return acc
  }, [] as Breadcrumb[])
})

const dynamicBreadcrumbsComputed = computed<Breadcrumb[]>(() => {
  const basePath = routeBreadcrumbs.value.at(-1)?.path || '/'

  return props.dynamicBreadcrumbs.map((item) => {
    return {
      ...item,
      path: `${basePath}/${item.path}`,
    }
  })
})

const breadcrumbs = computed<Breadcrumb[]>(() => {
  return [...routeBreadcrumbs.value, ...dynamicBreadcrumbsComputed.value]
})
</script>

<style lang="scss" scoped>
.view-header {
  display: block;
  background: var(--color-surface-bg);
  border-bottom: solid 1px var(--color-border-light);

  @media (min-width: config.$bp-desktop) {
    background: transparent;
    border-bottom: none;
  }

  &__content {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 1.5rem;
    width: 100%;
  }

  &__col {
    &--content {
      flex: 1 1 auto;
    }

    &--tools {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }
  }

  &__title {
    margin: 0;
  }

  &__footer {
    margin-top: var(--block-spacing-vertical-normal);

    @media (min-width: config.$bp-desktop) {
      margin-top: calc(var(--block-spacing-vertical-normal) * 2);
    }
  }
}

.breadcrumbs {
  display: block;
  width: 100%;
  margin-top: var(--block-spacing-vertical-normal);

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: var(--font-size-smaller);
  }

  &__list-item,
  &__item,
  &__separator {
    display: inline;
    padding: 0;
  }

  &__item {
    &--link {
      color: var(--color-primary);
      font-weight: var(--font-weight-bold);

      &:hover,
      &:focus-visible {
        @media (pointer: fine) {
          text-decoration: underline;
          text-underline-offset: 0.4em;
        }
      }
    }
  }

  &__separator {
    opacity: 0.5;
    margin: 0 0.5em;
  }
}
</style>
